import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import './navbar.css'
import { User, LogOut, Settings, Building, Briefcase } from 'lucide-react'
import { useSidebar } from '../../context/sidebar/SidebarContext'
import * as ProfileApi from '../../services/auth/Profile/ProfileApi'
import { useNavigate } from 'react-router-dom'
import NotificationV2 from '../NotificationV2/NotificationV2'
import { getPageTitleFromPath } from '../../utils/pathname'

const Navbar = () => {
  const { toggleSidebar, isSidebarOpen } = useSidebar()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    profile_picture: null,
    user_email: '',
    role_name: '',
  })
  const navigate = useNavigate()
  const location = useLocation()
  const pageTitle = getPageTitleFromPath(location.pathname)

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await ProfileApi.getProfile()
        if (userData) {
          setUserData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            profile_picture: userData.profile_picture || null,
            user_email: userData.user_email || '',
            role_name: userData.role_name || '',
          })
        } else {
          console.error('Invalid profile data structure received.')
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
      }
    }
    fetchProfile()
  }, [])

  useEffect(() => {
    const handleClickOutside = () => {
      setIsProfileOpen(false)
      setIsSettingsOpen(false)
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const toggleProfile = e => {
    e.stopPropagation()
    setIsProfileOpen(prev => !prev)
    setIsSettingsOpen(false)
  }

  const toggleSettings = e => {
    e.stopPropagation()
    setIsSettingsOpen(prev => !prev)
    setIsProfileOpen(false)
  }

  const handleLogout = () => {
    setIsProfileOpen(false)
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    navigate('/login')
  }

  const fullName = `${userData.first_name} ${userData.last_name}`.trim() || 'User'

  return (
    <nav className={`navbar ${!isSidebarOpen ? 'full-width' : ''}`}>
      <div className="navbar-left">
        <button className="menu-toggle" onClick={toggleSidebar}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 6H20M4 12H20M4 18H11"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="navbar-center">
        <h2 className="page-title">{pageTitle}</h2>
      </div>
      <div className="navbar-right">
        <NotificationV2 />
        {userData?.role_name === 'super-admin' && (
          <div className="icon-container" onClick={toggleSettings}>
            <Settings size={20} />
          </div>
        )}

        <div className="user-profile" onClick={toggleProfile}>
          {userData.profile_picture ? (
            <img src={userData.profile_picture} alt="Profile" className="profile-image" />
          ) : (
            <div className="profile-image-placeholder">
              <User size={20} />
            </div>
          )}
          <span className="user-name">{fullName}</span>
        </div>
      </div>

      {/* Profile Dropdown */}
      {isProfileOpen && (
        <div className="profile-dropdown" onClick={e => e.stopPropagation()}>
          <div className="profile-dropdown-header">
            <div className="profile-dropdown-user">
              {userData.profile_picture ? (
                <img
                  src={userData.profile_picture}
                  alt="Profile"
                  className="dropdown-profile-image"
                />
              ) : (
                <div className="dropdown-profile-placeholder">
                  <User size={24} />
                </div>
              )}
              <div className="profile-dropdown-info">
                <h4>{fullName}</h4>
                <p>{userData.user_email || 'No email available'}</p>
              </div>
            </div>
          </div>
          <div className="profile-dropdown-divider"></div>
          <div className="profile-dropdown-content">
            <Link
              to="/profile"
              className="profile-dropdown-item"
              onClick={() => setIsProfileOpen(false)}
            >
              <User size={18} />
              <span>View Profile</span>
            </Link>
            <button className="profile-dropdown-item logout-item" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}

      {/* Settings Dropdown */}
      {isSettingsOpen && (
        <div className="profile-dropdown settings-dropdown" onClick={e => e.stopPropagation()}>
          <div className="profile-dropdown-header">
            <h4>Settings</h4>
          </div>
          <div className="profile-dropdown-divider"></div>
          <div className="profile-dropdown-content">
            <Link
              to="/add-center"
              className="profile-dropdown-item"
              onClick={() => setIsSettingsOpen(false)}
            >
              <Building size={18} />
              <span>Center</span>
            </Link>
            <Link
              to="/create-organisation"
              className="profile-dropdown-item"
              onClick={() => setIsSettingsOpen(false)}
            >
              <Briefcase size={18} />
              <span>Organisation</span>
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
